import * as React from "react";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import { ContentContainer, Card } from "./common/StyledComponents";

export const GrundwasserDetailRoute = React.memo(
  function GrundwasserDetailRoute() {
    const { t } = useTexts();

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("grundwasser.detail.title", "Grund- und Quellwasser")}</h1>

          <Card>
            <h2>
              {t("grundwasser.detail.erklaert.title", "Grundwasser erklärt")}
            </h2>
            <p>
              {t(
                "grundwasser.detail.erklaert.text",
                "Grundwasser ist unterirdisch angesammeltes Wasser, das sich in den Poren, Klüften und Hohlräumen des Erdreichs befindet. Es entsteht hauptsächlich durch das Versickern von Niederschlägen und Oberflächenwasser in den Boden."
              )}
            </p>
            <h3>
              {t(
                "grundwasser.detail.erklaert.eigenschaften.title",
                "Wichtige Eigenschaften"
              )}
            </h3>
            <ul>
              <li>
                {t(
                  "grundwasser.detail.erklaert.eigenschaften.item1",
                  "Natürliche Filterung durch Bodenschichten"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.erklaert.eigenschaften.item2",
                  "Gleichmäßige Temperatur das ganze Jahr über"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.erklaert.eigenschaften.item3",
                  "Meist hohe Qualität und wenig Schadstoffe"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.erklaert.eigenschaften.item4",
                  "Große Speicherkapazität in unterirdischen Reservoiren"
                )}
              </li>
            </ul>
          </Card>

          <Card>
            <h2>
              {t(
                "grundwasser.detail.unterschied.title",
                "Unterschied Grundwasser und Quellwasser"
              )}
            </h2>
            <h3>
              {t(
                "grundwasser.detail.unterschied.grundwasser.title",
                "Grundwasser"
              )}
            </h3>
            <ul>
              <li>
                {t(
                  "grundwasser.detail.unterschied.grundwasser.item1",
                  "Befindet sich in unterirdischen Schichten"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.grundwasser.item2",
                  "Muss durch Brunnen oder Pumpen gefördert werden"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.grundwasser.item3",
                  "Große Mengen verfügbar"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.grundwasser.item4",
                  "Konstante Verfügbarkeit"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.grundwasser.item5",
                  "Benötigt technische Förderung"
                )}
              </li>
            </ul>
            <h3>
              {t(
                "grundwasser.detail.unterschied.quellwasser.title",
                "Quellwasser"
              )}
            </h3>
            <ul>
              <li>
                {t(
                  "grundwasser.detail.unterschied.quellwasser.item1",
                  "Tritt natürlich an der Erdoberfläche aus"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.quellwasser.item2",
                  "Fließt selbstständig aus dem Boden"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.quellwasser.item3",
                  "Meist begrenzte Mengen"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.quellwasser.item4",
                  "Kann saisonal schwanken"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.unterschied.quellwasser.item5",
                  "Natürlicher Austritt ohne Pumpen"
                )}
              </li>
            </ul>
          </Card>

          <Card>
            <h2>
              {t(
                "grundwasser.detail.foerderung.grundwasser.title",
                "Förderung des Grundwassers"
              )}
            </h2>
            <p>
              {t(
                "grundwasser.detail.foerderung.grundwasser.intro",
                "Die Grundwasserförderung erfolgt durch verschiedene Methoden:"
              )}
            </p>
            <ul>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.grundwasser.item1.label",
                    "Brunnen:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.grundwasser.item1.text",
                  "Vertikale Bohrungen in grundwasserführende Schichten"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.grundwasser.item2.label",
                    "Horizontalfilterbrunnen:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.grundwasser.item2.text",
                  "Für größere Fördermengen"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.grundwasser.item3.label",
                    "Pumpstationen:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.grundwasser.item3.text",
                  "Elektrische oder solarbetriebene Pumpen"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.grundwasser.item4.label",
                    "Artesische Brunnen:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.grundwasser.item4.text",
                  "Selbstfließende Brunnen bei Druckwasser"
                )}
              </li>
            </ul>
            <h3>
              {t(
                "grundwasser.detail.foerderung.grundwasser.aspekte.title",
                "Wichtige Aspekte bei der Förderung"
              )}
            </h3>
            <ul>
              <li>
                {t(
                  "grundwasser.detail.foerderung.grundwasser.aspekte.item1",
                  "Schutz vor Übernutzung und Absenkung des Grundwasserspiegels"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.grundwasser.aspekte.item2",
                  "Qualitätskontrolle und Schutz vor Verunreinigungen"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.grundwasser.aspekte.item3",
                  "Genehmigungsverfahren und behördliche Überwachung"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.grundwasser.aspekte.item4",
                  "Nachhaltiges Wassermanagement"
                )}
              </li>
            </ul>
          </Card>

          <Card>
            <h2>
              {t(
                "grundwasser.detail.foerderung.quellwasser.title",
                "Förderung des Quellwassers"
              )}
            </h2>
            <p>
              {t(
                "grundwasser.detail.foerderung.quellwasser.intro",
                "Quellwasser erfordert andere Fördermaßnahmen als Grundwasser:"
              )}
            </p>
            <ul>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.quellwasser.item1.label",
                    "Quellfassung:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.quellwasser.item1.text",
                  "Bauwerke zum Sammeln des austretenden Wassers"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.quellwasser.item2.label",
                    "Schutzgebiete:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.quellwasser.item2.text",
                  "Einrichtung von Wasserschutzgebieten um die Quelle"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.quellwasser.item3.label",
                    "Leitungssysteme:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.quellwasser.item3.text",
                  "Transport des Wassers zu Verbrauchern"
                )}
              </li>
              <li>
                <strong>
                  {t(
                    "grundwasser.detail.foerderung.quellwasser.item4.label",
                    "Speicherbecken:"
                  )}
                </strong>{" "}
                {t(
                  "grundwasser.detail.foerderung.quellwasser.item4.text",
                  "Ausgleich von Schwankungen in der Schüttung"
                )}
              </li>
            </ul>
            <h3>
              {t(
                "grundwasser.detail.foerderung.quellwasser.vorteile.title",
                "Vorteile von Quellwasser"
              )}
            </h3>
            <ul>
              <li>
                {t(
                  "grundwasser.detail.foerderung.quellwasser.vorteile.item1",
                  "Oft sehr hohe natürliche Qualität"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.quellwasser.vorteile.item2",
                  "Keine Förderkosten durch Pumpen"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.quellwasser.vorteile.item3",
                  "Umweltfreundliche Gewinnung"
                )}
              </li>
              <li>
                {t(
                  "grundwasser.detail.foerderung.quellwasser.vorteile.item4",
                  "Traditionelle und bewährte Wasserversorgung"
                )}
              </li>
            </ul>
          </Card>
        </ContentContainer>
      </AppLayout>
    );
  }
);
